import { ComponentProps, FC, useEffect } from 'react'
import { z } from 'zod'
import { useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import useSignal from '$actions/useSignal'
import queryParams from '$store/queryParams'

import Phone from '$hook-fields/Phone'

import StatusMessage from '$elements/StatusMessage'
import { TypeSubmitForm } from '$hook-forms/types'
import {
  captchaString,
  requiredString,
  requiredTrimmedString
} from '$hook-forms/general-validators'
import useDefaultPhoneCode from '$hook-forms/hooks/useDefaultPhoneCode'

import { submit } from '$forms/common/style.css'
import { allowedWhatsappCountries, callingCodes } from '$constants/country-data'

import { container, description, otpOptionContainer } from './styles.css'
import { Checkbox } from '@trustycars/genie-ui/components'

import WhatsappLogo from '$icons/WhatsappLogo'
import CFTurnstile from '$hook-fields/CFTurnstile'

const Schema = z.object({
  phone_code: requiredString,
  phone_no: requiredTrimmedString.min(5, {
    message: 'At least 5 digits required'
  }),
  cf_turnstile_token: captchaString,
  is_whatsapp: z.boolean()
})

const resolver = zodResolver(Schema)

type TFields = z.infer<typeof Schema>

const checkCanUseWhatsapp = (phone_code: string) => {
  return callingCodes
    .filter(({ id }) => allowedWhatsappCountries.includes(id))
    .some(({ code }) => code === +phone_code)
}

export type MobileOTPRequestFormFields = TFields

export type MobileOTPRequestFormProps = TypeSubmitForm<TFields> & {
  errorMessage?: ComponentProps<typeof StatusMessage>['children']
}

export const MobileOTPRequestForm: FC<MobileOTPRequestFormProps> = ({
  onSuccess,
  disabled,
  errorMessage
}) => {
  const {
    params: { auth_phone_number }
  } = useSignal(queryParams)

  const phone_code = useDefaultPhoneCode()

  const defaultValues: TFields = {
    phone_code,
    phone_no: `${auth_phone_number ?? ''}`,
    is_whatsapp: checkCanUseWhatsapp(phone_code),
    cf_turnstile_token: undefined
  }

  const { control, reset, watch, handleSubmit, formState } = useForm<TFields>({
    resolver,
    defaultValues,
    mode: 'all'
  })

  const isInvalid = disabled || !!Object.keys(formState.errors).length

  const canUseWhatsapp = checkCanUseWhatsapp(watch('phone_code'))

  const { field } = useController({ name: 'is_whatsapp', control })
  const { onChange } = field

  useEffect(() => {
    onChange(canUseWhatsapp)
  }, [onChange, canUseWhatsapp])

  return (
    <form
      className={container}
      onSubmit={handleSubmit((...handler) => onSuccess(reset, ...handler))}
      noValidate
      autoComplete="off"
    >
      {errorMessage && (
        <StatusMessage kind="danger">{errorMessage}</StatusMessage>
      )}

      <Phone
        control={control}
        callingCodes={callingCodes}
        name="phone_no"
        name2="phone_code"
        label="Phone Number"
        required
        inputProps={{ placeholder: 'XXXX XXXX XXXX' }}
        disabled={disabled}
      />

      <CFTurnstile
        control={control}
        name="cf_turnstile_token"
        disabled={disabled}
      />

      <button type="submit" className={submit} disabled={isInvalid}>
        Get OTP
      </button>

      {canUseWhatsapp && (
        <article className={otpOptionContainer}>
          <article style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Checkbox
              label="Get Code by Whatsapp"
              boxProps={{
                checked: field.value,
                onCheckedChange: field.onChange
              }}
            />
            <WhatsappLogo />
          </article>

          <p className={description}>
            If you have whatsapp with this phone number.
          </p>
        </article>
      )}
    </form>
  )
}

export default MobileOTPRequestForm
